import { addDisplayName, addHandlers, addProps, flowMax } from 'ad-hok'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import cx from 'classnames'
import { addApi, addUser } from 'shared/src/api'
import { addDataLoadingFromPromise } from 'shared/src/utils/dataLoading'
import { Trans } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  addNavigate,
  addOptionalIntPathParam,
} from 'shared/src/utils/addNavigate'
import { addDays, format } from 'date-fns'
import { RedemptionRequestStatus } from 'shared/src/api/types'

import { Button } from 'components/Button'
import { Page } from 'components/Page'
import { addCmsLoadingAndErrorIndicators } from 'utils/addCmsLoadingAndErrorIndicators'
import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { SANS_SERIF_FONT_FAMILY, SERIF_FONT_FAMILY } from 'components/styles'
import backgroundImage from '../../assets/down-chevron.svg'
import { Balance } from './Balance'
import CreatorFundsGrid from './CreatorFundsGrid'

const CREATOR_FUND_INFO_SITE = 'https://www.shoprodeo.com/creator-fund-faqs'

const formatDate = (date: string) =>
  format(addDays(new Date(date), 1), 'MM.dd.yyyy')

export const getRedemptionDisplayStatus = (
  redemptionStatus: RedemptionRequestStatus
) => {
  switch (redemptionStatus) {
    case 'unprocessed':
      return 'pending'
    case 'processing':
      return 'pending'
    default:
      return redemptionStatus
  }
}

const statusColor = (displayText: string) => {
  switch (displayText) {
    case 'pending':
      return '#E79740'
    case 'fulfilled':
      return '#4E5DD3'
    case 'error':
      return '#F57561'
    case 'bonus':
      return '#3D9050'
    case 'partnership':
      return '#D281F0'
    default:
      return '#E79740'
  }
}

const useStyles = createUseStyles({
  headerContainer: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: 24,
    paddingRight: 24,
    paddingBottom: 13,
    borderBottom: '1px solid rgba(144, 115, 79, 0.08)',
  },
  title: {
    fontFamily: SERIF_FONT_FAMILY,
    fontSize: 24,
    fontWeight: 700,
    lineHeight: '140%',
    letterSpacing: 0.4,
    color: '#232323',
  },
  subtitle: {
    fontFamily: SANS_SERIF_FONT_FAMILY,
    fontSize: 14,
    fontWeight: 700,
    lineHeight: '160%',
    letterSpacing: 0.2,
    color: '#90734F',
    opacity: 0.8,
  },
  creatorFundDescription: {
    maxWidth: 198,
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  underline: {
    textDecoration: 'underline',
  },
  select: {
    width: 144,
    height: 48,
    background: 'rgba(54, 52, 50, 0.02)',
    border: '1px solid rgba(144, 115, 79, 0.24)',
    borderRadius: 2,
    fontFamily: SANS_SERIF_FONT_FAMILY,
    fontSize: 16,
    color: 'rgba(54, 52, 50, 0.8)',
    backgroundImage: `url(${backgroundImage})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: '115px 16px',
    marginTop: 33,
    alignSelf: 'flex-end',
  },
  tableRowDateText: {
    color: (displayText: string) => statusColor(displayText),
  },
  tableBadgeContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 22,
  },
  badge: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: 400,
    lineHeight: '140%',
    fontFamily: SANS_SERIF_FONT_FAMILY,
    height: 18,
    paddingLeft: 8,
    paddingRight: 8,
    borderRadius: 2,
    background: (displayText: string) => statusColor(displayText),
  },
})

interface BadgeProps {
  displayText: string
  date?: string
  className?: string
}

export const Badge: FC<BadgeProps> = flowMax(
  addDisplayName('Badge'),
  addProps(({ displayText }) => ({
    classes: useStyles(displayText),
  })),
  ({ displayText, date, classes, className }) => (
    <div className={classes.tableBadgeContainer}>
      {date && (
        <div className={classes.tableRowDateText}>{formatDate(date)}</div>
      )}
      <div className={cx(classes.badge, className)}>{displayText}</div>
    </div>
  )
)

export const CreatorFund: FC = flowMax(
  addDisplayName('CreatorFund'),
  addApi,
  addUser,
  addOptionalIntPathParam('userId'),
  addDataLoadingFromPromise(
    'creatorEarnings',
    ({ userId, user, api }) =>
      api.creatorEarnings.creatorOverview(userId ?? user.id),
    []
  ),
  addCmsLoadingAndErrorIndicators('creatorEarnings'),
  addNavigate,
  addHandlers({
    onClickRedemption:
      ({ userId, navigate }) =>
      (id: number) => {
        navigate(
          userId
            ? `/creatorEarnings/user/${userId}/redeem/${id}`
            : `/earnings/redeem/${id}`
        )
      },
    onRedeem:
      ({ userId, navigate }) =>
      () =>
        navigate(
          userId ? `/creatorEarnings/user/${userId}/redeem` : '/earnings/redeem'
        ),
  }),
  addTranslationHelpers,
  addProps(() => ({
    classes: useStyles(),
  })),
  ({ creatorEarnings, onRedeem, classes, t, userId }) => (
    <Page>
      <div className={classes.headerContainer}>
        <div className={classes.headerRow}>
          <div className={classes.title}>{t('admin.creatorFund.title')}</div>
          <Button
            icon="add"
            text={t('admin.creatorFund.redeem')}
            onClick={onRedeem}
            disabled={creatorEarnings.availableBalance <= 0}
          />
        </div>
        <div className={cx(classes.subtitle, classes.creatorFundDescription)}>
          <Trans t={t} i18nKey="admin.creatorFund.availableBalanceDescription">
            <Link
              to={CREATOR_FUND_INFO_SITE}
              className={classes.underline}
              target="_blank"
            />
          </Trans>
        </div>
        <Balance
          amount={creatorEarnings.totalEarnings}
          label={t('admin.creatorFund.totalEarned')}
          gold
        />
        <div className={classes.headerRow}>
          <Balance
            amount={creatorEarnings.currentBalance}
            label={t('admin.creatorFund.currentBalance')}
          />
          <Balance
            amount={creatorEarnings.availableBalance}
            label={t('admin.creatorFund.availableBalance')}
          />
        </div>
      </div>
      {creatorEarnings.transactions.length > 0 && (
        <CreatorFundsGrid
          rowData={creatorEarnings.transactions}
          userId={userId}
        />
      )}
    </Page>
  )
)
